/**
 * Email System Types
 * Shared types for email templates, logs, and settings
 */

import { z } from 'zod/v4'

export type EmailLogStatus = 'pending' | 'queued' | 'sent' | 'delivered' | 'failed' | 'bounced'

/** `capture`: the dev/test transport (messages are kept, never sent). */
export type EmailProviderName = 'capture' | 'console' | 'resend' | 'sendgrid' | 'ses' | 'smtp'

export interface EmailVariable {
  name: string
  description?: string
  required?: boolean
  example?: string
}

export interface EmailTemplate {
  id: string
  tenantId: string | null
  /** Name of the organization that owns it (`null`: a platform/system template). */
  tenantName?: string | null
  code: string
  name: string
  subject: string
  bodyHtml: string
  bodyText: string | null
  variables: EmailVariable[] | null
  isActive: boolean
  isSystem: boolean
  createdAt: string
  updatedAt: string
  /** `null` in the seeded system templates (the column is nullable). */
  createdBy: string | null
  lastModifiedBy: string | null
}

/** Where the configuration that sent (or refused) an e-mail came from (#2817). */
export type EmailDeliverySource = 'tenant' | 'platform' | 'console_fallback' | 'none'

/** Why nothing could send it (`deliverySource = 'none'`). */
export type EmailDeliveryFailureReason =
  | 'not_configured'
  | 'platform_disabled'
  | 'console_in_production'

/** `error_message` of an e-mail no provider could send. */
export const EMAIL_PROVIDER_NOT_CONFIGURED = 'EMAIL_PROVIDER_NOT_CONFIGURED'

/** Code of a provider that is stored but not wired in BAT (SendGrid, SES, SMTP): saving it, or sending with it. */
export const EMAIL_PROVIDER_NOT_IMPLEMENTED = 'EMAIL_PROVIDER_NOT_IMPLEMENTED'

/** Code of an e-mail the provider (Resend...) refused: the provider's own words are in the log row, not in the error. */
export const EMAIL_PROVIDER_REJECTED = 'EMAIL_PROVIDER_REJECTED'

/** `error_message` of an e-mail the suppression list stopped. */
export const EMAIL_RECIPIENT_SUPPRESSED = 'EMAIL_RECIPIENT_SUPPRESSED'

/**
 * Code of an e-mail (or of a provider test) that could not go out because the
 * stored provider key cannot be decrypted — an older release left it in clear,
 * it was altered, or the master key changed. The fix is to save it again.
 */
export const EMAIL_PROVIDER_KEY_UNREADABLE = 'EMAIL_PROVIDER_KEY_UNREADABLE'

export interface EmailLog {
  id: string
  /** The organization the e-mail belongs to (the log is platform-wide). */
  tenantId?: string
  tenantName?: string | null
  recipientEmail: string
  recipientUserId?: string
  subject: string
  templateCode: string | null
  status: EmailLogStatus
  providerName: string | null
  providerMessageId?: string
  errorMessage: string | null
  retryCount: number
  maxRetries: number
  variables: Record<string, unknown> | null
  metadata: Record<string, unknown> | null
  sentAt: string | null
  deliveredAt: string | null
  failedAt: string | null
  queuedAt: string | null
  jobId: string | null
  createdAt: string
  updatedAt: string
  template?: EmailTemplate
  // New engagement tracking fields
  openedAt: string | null
  openCount: number
  clickedAt: string | null
  clickCount: number
  lastWebhookAt: string | null
}

export interface EmailSettings {
  id?: string
  provider: EmailProviderName
  fromEmail?: string
  fromName?: string
  replyToEmail?: string
  /** `false` = «Sin envío»: the row keeps provider and key but sends nothing. */
  isActive: boolean
  /** A key is stored; the key itself never leaves the server. */
  hasApiKey?: boolean
  /** Last four characters of a stored key (only when it is long enough). */
  apiKeyLast4?: string
  updatedAt?: string
  updatedBy?: { id: string; email: string } | null
  lastChangedFields?: string[]
  /**
   * Resultado de la ÚLTIMA prueba de conexión real, tal y como lo guarda
   * `email-settings.service.ts#testConnection`. El backend ya lo enviaba en
   * `GET /api/admin/email/settings`; este contrato no lo declaraba, así que la
   * pantalla no tenía forma tipada de leer el único hecho de conexión que
   * existe —y derivaba «⚡ Connected» de `apiKey.length > 10`—.
   *
   * ⚠️ Viaja por JSON: las fechas llegan como cadenas ISO, no como `Date`.
   */
  lastTestedAt?: string | null
  lastTestResult?: {
    success: boolean
    message?: string
    timestamp: string
    outcome?: 'sent' | 'console' | 'failed'
    recipient?: string
    providerMessageId?: string
    logId?: string
    durationMs?: number
  } | null
}

/** A field of the platform provider form, as the server names what a save changed. */
export type EmailSettingsField = 'provider' | 'apiKey' | 'fromEmail' | 'fromName' | 'replyToEmail'

/** `GET /api/admin/email/settings`: the PLATFORM provider and what it reaches. */
export interface PlatformEmailSettingsView {
  configured: boolean
  settings: EmailSettings | null
  defaults: { fromEmail: string; fromName: string }
  /** What an organization WITHOUT its own provider gets today. */
  effective: {
    source: EmailDeliverySource
    provider: EmailProviderName | null
    reason?: EmailDeliveryFailureReason
  }
  scope: {
    organizations: number
    inheriting: number
    ownProvider: number
    ownProviderOrganizations: Array<{ id: string; name: string }>
  }
  environment: { production: boolean; selectableProviders: Array<EmailProviderName | 'none'> }
}

/** `POST /api/admin/email/settings/test` */
export interface EmailProviderTestResult {
  outcome: 'sent' | 'console' | 'failed'
  success: boolean
  provider: EmailProviderName | null
  recipient: string
  providerMessageId?: string
  message?: string
  logId?: string
  durationMs: number
  testedAt: string
}

/** One row of the platform e-mail log. */
export interface EmailLogSummary {
  id: string
  tenantId: string
  tenantName: string | null
  recipientEmail: string
  subject: string
  templateCode: string | null
  templateName: string | null
  status: EmailLogStatus
  providerName: string | null
  deliverySource: EmailDeliverySource | null
  errorMessage: string | null
  /** Why the suppression list stopped this e-mail (`bounced`, `complained`, …); `null` when it did not. */
  suppressionReason?: string | null
  /** The suppression reason the e-mail was sent despite (an account-access or security e-mail); else `null`. */
  suppressionBypassed?: string | null
  queuedAt: string | null
  sentAt: string | null
  deliveredAt: string | null
  failedAt: string | null
  createdAt: string
  updatedAt: string
}

/** The log's status strip: every count is also a filter. */
export interface EmailLogStripCounts {
  total: number
  failed: number
  noProvider: number
  unacknowledged: number
  delivered: number
  bounced: number
  inProgress: number
  period?: { days: number | null; from: string | null; to: string | null }
}

export interface EmailLogFacets {
  organizations: Array<{ id: string; name: string; count: number }>
  templates: Array<{ code: string; name: string | null; count: number }>
}

export interface EmailStats {
  total: number
  pending: number
  queued: number
  sent: number
  delivered: number
  failed: number
  bounced: number
}

// Enhanced analytics types
export interface EnhancedEmailStats extends EmailStats {
  openRate: number
  clickRate: number
  uniqueOpens: number
  uniqueClicks: number
  deliveryRate: number
}

/**
 * One template's e-mails in the period (#4230). The counts have the SAME definitions as the status strip of
 * the platform log (`EmailLogStripCounts`): `unacknowledged` = `sent` without `delivered_at`, `noProvider` =
 * `failed` for lack of a configured provider (a subset of `failed`), `inProgress` = `pending` + `queued`.
 */
export interface TemplateAnalytics {
  /** `null` for e-mails sent without a template. */
  templateCode: string | null
  /** The template's name; `null` when it was deleted or the e-mail has no template. */
  templateName: string | null
  total: number
  delivered: number
  unacknowledged: number
  failed: number
  noProvider: number
  bounced: number
  inProgress: number
  /** `delivered / (total − inProgress) × 100`, one decimal; `null` when nothing is left to rate. */
  deliveryRate: number | null
  /** ISO instant of the template's latest e-mail in the period. */
  lastSentAt: string | null
  totalOpens: number
  totalClicks: number
  uniqueOpens: number
  uniqueClicks: number
  openRate: number
  clickRate: number
}

/** The seven counts of the strip, over every e-mail of the period (also the templates cut by `truncated`). */
export interface TemplateAnalyticsTotals {
  total: number
  delivered: number
  unacknowledged: number
  failed: number
  noProvider: number
  bounced: number
  inProgress: number
}

/**
 * The period the figures cover: `days` as asked (echoed even when an explicit `from`/`to` overrides it, as the log's
 * strip does) and the instants it resolves to (ISO); all `null` = whole history.
 */
export interface TemplateAnalyticsPeriod {
  days: number | null
  from: string | null
  to: string | null
}

/** `GET /api/admin/email/analytics/templates`. */
export interface TemplateAnalyticsResult {
  period: TemplateAnalyticsPeriod
  totals: TemplateAnalyticsTotals
  /** At most 200, most e-mails first (ties by code). */
  templates: TemplateAnalytics[]
  /** More templates than the 200 listed: `totals` still counts all of them. */
  truncated: boolean
}

export interface AnalyticsFilter {
  from?: string
  to?: string
  templateCode?: string
}

/** Bucket size of the delivery trend: one bar per local day, or per week (Monday) for long periods. */
export type DeliveryTrendInterval = 'day' | 'week'

/**
 * One bar of «Tendencia de entrega»: the five stacked groups of the status strip. `failed` includes the
 * «no provider» failures (as in the strip); `noProvider` is only in `DeliveryTrend.totals`.
 */
export interface DeliveryTrendPoint {
  /** `YYYY-MM-DD` on the profile's clock; the Monday of the week with `interval: 'week'`. */
  date: string
  total: number
  delivered: number
  unacknowledged: number
  inProgress: number
  failed: number
  bounced: number
}

/** `GET /api/admin/email/analytics/trend`: one point per bucket of the period, empty ones as explicit zeros. */
export interface DeliveryTrend {
  period: {
    /** ISO instants the figures cover (the same window the status strip reads). */
    from: string
    to: string
    /** IANA zone of the profile that cut the days. */
    timeZone: string
    interval: DeliveryTrendInterval
  }
  /** The seven counts of the strip over the whole period: they add up to «Registro». */
  totals: TemplateAnalyticsTotals
  /** Oldest first. */
  points: DeliveryTrendPoint[]
}

const deliveryTrendCountSchema = z.number().int().min(0)

const deliveryTrendPointSchema = z.object({
  date: z.iso.date(),
  total: deliveryTrendCountSchema,
  delivered: deliveryTrendCountSchema,
  unacknowledged: deliveryTrendCountSchema,
  inProgress: deliveryTrendCountSchema,
  failed: deliveryTrendCountSchema,
  bounced: deliveryTrendCountSchema,
})

export const deliveryTrendSchema = z.object({
  period: z.object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    timeZone: z.string().min(1),
    interval: z.enum(['day', 'week']),
  }),
  totals: z.object({
    total: deliveryTrendCountSchema,
    delivered: deliveryTrendCountSchema,
    unacknowledged: deliveryTrendCountSchema,
    failed: deliveryTrendCountSchema,
    noProvider: deliveryTrendCountSchema,
    bounced: deliveryTrendCountSchema,
    inProgress: deliveryTrendCountSchema,
  }),
  points: z.array(deliveryTrendPointSchema),
}) satisfies z.ZodType<DeliveryTrend> // the response schema cannot drift from the interface

// Suppression types
export interface EmailSuppression {
  id: string
  tenantId: string
  /** Name of the organization the row belongs to (the list is per organization). Null when it has none. */
  tenantName?: string | null
  email: string
  userId?: string
  reason: 'bounced' | 'complained' | 'blocked' | 'invalid'
  subReason?: string
  providerName?: string
  providerCode?: string
  metadata?: Record<string, unknown>
  /** `manual` when an operator added the row, `provider` when a webhook did. */
  source?: 'manual' | 'provider'
  /** The operator's one-line note of a manual row; null for the rest. */
  note?: string | null
  expiresAt?: string
  createdAt: string
}

/** Body of `POST /api/admin/email/suppressions`: an operator puts one address on one organization's list. */
export interface AddEmailSuppressionInput {
  email: string
  tenantId: string
  reason: EmailSuppression['reason']
  /** One line, up to 140 characters, no `@` and no control characters. Optional. */
  note?: string
}

export interface CreateSuppressionOptions {
  email: string
  reason: 'bounced' | 'complained' | 'blocked' | 'invalid'
  subReason?: string
  userId?: string
  providerName?: string
  providerCode?: string
  metadata?: Record<string, unknown>
  expiresAt?: string
}
